import { create } from 'zustand';

import { send } from '@/api/send';
import type {
  AccountWithBalance,
  CategoryGroupEntity,
  PayeeEntity,
} from '@/api/types';

type AppState = {
  ready: boolean;
  syncing: boolean;
  serverVersion: string;
  accounts: AccountWithBalance[];
  categoryGroups: CategoryGroupEntity[];
  payees: PayeeEntity[];
  load: () => Promise<void>;
  refreshAccounts: () => Promise<void>;
  runSync: () => Promise<void>;
};

/**
 * Stands in for `#accounts`, `#payees` and `#app` slices: the shell-level data
 * every page reads (sidebar account list, category names in the register).
 */
export const useAppStore = create<AppState>((set, get) => ({
  ready: false,
  syncing: false,
  serverVersion: '',
  accounts: [],
  categoryGroups: [],
  payees: [],

  async load() {
    const [accounts, categoryGroups, payees, serverVersion] = await Promise.all([
      send('accounts-get'),
      send('categories-get'),
      send('payees-get'),
      send('get-server-version'),
    ]);
    set({ accounts, categoryGroups, payees, serverVersion, ready: true });
  },

  async refreshAccounts() {
    set({ accounts: await send('accounts-get') });
  },

  async runSync() {
    if (get().syncing) return;
    set({ syncing: true });
    await send('sync');
    await get().refreshAccounts();
    set({ syncing: false });
  },
}));

export function useAccounts(): AccountWithBalance[] {
  return useAppStore(state => state.accounts);
}

type UiState = {
  hideAmounts: boolean;
  toggleHideAmounts: () => void;
};

export const useUiStore = create<UiState>(set => ({
  hideAmounts: false,
  toggleHideAmounts: () => set(state => ({ hideAmounts: !state.hideAmounts })),
}));
